import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  stockTransferService,
  StockTransfer,
} from "../../services/stock-transfer.service";
import { showToast } from "../../lib/toast";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-800",
  APPROVED: "bg-blue-100 text-blue-800",
  IN_TRANSIT: "bg-yellow-100 text-yellow-800",
  RECEIVED: "bg-green-100 text-green-800",
  CANCELLED: "bg-red-100 text-red-800",
  REJECTED: "bg-red-100 text-red-800",
};

export default function StockTransferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState<StockTransfer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadTransfer(id);
    }
  }, [id]);

  const loadTransfer = async (transferId: string) => {
    try {
      setLoading(true);
      const data = await stockTransferService.getById(transferId);
      setTransfer(data);
    } catch (error) {
      showToast.error("Failed to load stock transfer details");
      navigate("/stock-transfers");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="p-6 text-center text-gray-500">
        Transfer not found
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate("/stock-transfers")}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-1"
          >
            ← Back to Stock Transfers
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Stock Transfer {transfer.transferNumber}
          </h1>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[transfer.status] || "bg-gray-100 text-gray-800"}`}
        >
          {transfer.status.replace(/_/g, " ")}
        </span>
      </div>

      {/* Transfer Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Transfer Details
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Transfer Number</p>
            <p className="font-medium">{transfer.transferNumber}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Date</p>
            <p className="font-medium">
              {new Date(transfer.transferDate).toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">From Store</p>
            <p className="font-medium">
              {transfer.sourceStore?.name || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">To Store</p>
            <p className="font-medium">
              {transfer.destStore?.name || "N/A"}
            </p>
          </div>
          {transfer.approvedAt && (
            <div>
              <p className="text-sm text-gray-500">Approved At</p>
              <p className="font-medium">
                {new Date(transfer.approvedAt).toLocaleString()}
              </p>
            </div>
          )}
          {transfer.sentAt && (
            <div>
              <p className="text-sm text-gray-500">Sent At</p>
              <p className="font-medium">
                {new Date(transfer.sentAt).toLocaleString()}
              </p>
            </div>
          )}
          {transfer.receivedAt && (
            <div>
              <p className="text-sm text-gray-500">Received At</p>
              <p className="font-medium">
                {new Date(transfer.receivedAt).toLocaleString()}
              </p>
            </div>
          )}
          {transfer.notes && (
            <div className="col-span-2">
              <p className="text-sm text-gray-500">Notes</p>
              <p className="font-medium">{transfer.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Transfer Items ({transfer.items?.length || 0})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Batch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Expiry
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Requested
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Sent
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Received
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Rejected
                </th>
                {transfer.items?.some((i) => i.rejectionReason) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Rejection Reason
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transfer.items?.map((item, index) => (
                <tr
                  key={item.id}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.product?.name || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.product?.sku || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.batch?.batchNumber || "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.batch?.expiryDate
                      ? new Date(item.batch.expiryDate).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {item.qtyRequested}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {item.qtySent}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {item.qtyReceived}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span
                      className={
                        item.qtyRejected > 0
                          ? "text-red-600 font-semibold"
                          : "text-gray-500"
                      }
                    >
                      {item.qtyRejected}
                    </span>
                  </td>
                  {transfer.items?.some((i) => i.rejectionReason) && (
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.rejectionReason || "-"}
                    </td>
                  )}
                </tr>
              ))}
              {(!transfer.items || transfer.items.length === 0) && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
